import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode
} from 'react';
import AuthService from '../services/AuthService';

interface AuthContextType {
  userId: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  clearUserId: () => Promise<void>;
  regenerateUserId: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  console.log('=== AuthProvider: Initializing ===');
  
  const authServiceRef = useRef(AuthService.getInstance());
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('AuthProvider: Setting up Firebase auth listener');
    const authService = authServiceRef.current;

    const unsubscribe = authService.initialize((uid) => {
      console.log('AuthProvider: Auth state changed:', { uid });
      setUserId(uid);
      setLoading(false);
    });

    return () => {
      console.log('AuthProvider: Cleaning up auth listener');
      unsubscribe?.();
    };
  }, []);
  
  // Log userId changes
  useEffect(() => {
    console.log('AuthProvider: UserId state changed:', {
      userId,
      isAuthenticated: !!userId,
      loading
    });
  }, [userId, loading]);

  const clearUserId = useCallback(async () => {
    console.log('AuthProvider: Clearing userId');
    setLoading(true);
    await authServiceRef.current.clearUserId();
    console.log('AuthProvider: clearUserId complete');
  }, []);

  const regenerateUserId = useCallback(async () => {
    console.log('AuthProvider: Regenerating userId');
    setLoading(true);
    const newUserId = await authServiceRef.current.regenerateUserId();
    console.log('AuthProvider: New userId generated:', newUserId);
    return newUserId;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        userId,
        isAuthenticated: !!userId,
        loading,
        clearUserId,
        regenerateUserId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
